import { PROJECT_STATUSES, type ProjectStatus, type CustomerListItem } from '../types';

interface ProjectFiltersProps {
  filters: {
    status?: ProjectStatus;
    customerId?: string;
    search?: string;
  };
  sorting: {
    field: 'created_at' | 'start_date' | 'customer_name';
    direction: 'asc' | 'desc';
  };
  customers: CustomerListItem[];
  onFilterChange: (filters: {
    status?: ProjectStatus;
    customerId?: string;
    search?: string;
  }) => void;
  onSortingChange: (sorting: {
    field: 'created_at' | 'start_date' | 'customer_name';
    direction: 'asc' | 'desc';
  }) => void;
  onClear: () => void;
}

export function ProjectFilters({
  filters,
  sorting,
  customers,
  onFilterChange,
  onSortingChange,
  onClear,
}: ProjectFiltersProps) {
  const hasActiveFilters =
    filters.status || filters.customerId || filters.search;

  return (
    <div className="bg-white rounded-xl border border-secondary-200 p-4">
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search */}
        <div className="flex-1">
          <label htmlFor="search" className="sr-only">
            Search projects
          </label>
          <div className="relative">
            <input
              id="search"
              type="text"
              value={filters.search || ''}
              onChange={(e) =>
                onFilterChange({ ...filters, search: e.target.value || undefined })
              }
              placeholder="Search by project, customer, or address..."
              className="input-field pl-10"
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Status Filter */}
        <div className="w-full lg:w-40">
          <label htmlFor="statusFilter" className="sr-only">
            Filter by status
          </label>
          <select
            id="statusFilter"
            value={filters.status || ''}
            onChange={(e) =>
              onFilterChange({
                ...filters,
                status: (e.target.value as ProjectStatus) || undefined,
              })
            }
            className="input-field"
          >
            <option value="">All Statuses</option>
            {PROJECT_STATUSES.map((status) => (
              <option key={status.value} value={status.value}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        {/* Customer Filter */}
        <div className="w-full lg:w-48">
          <label htmlFor="customerFilter" className="sr-only">
            Filter by customer
          </label>
          <select
            id="customerFilter"
            value={filters.customerId || ''}
            onChange={(e) =>
              onFilterChange({
                ...filters,
                customerId: e.target.value || undefined,
              })
            }
            className="input-field"
          >
            <option value="">All Customers</option>
            {customers.map((customer) => (
              <option key={customer.id} value={customer.id}>
                {customer.full_name}
              </option>
            ))}
          </select>
        </div>

        {/* Sort */}
        <div className="w-full lg:w-48">
          <label htmlFor="sortField" className="sr-only">
            Sort by
          </label>
          <select
            id="sortField"
            value={`${sorting.field}-${sorting.direction}`}
            onChange={(e) => {
              const [field, direction] = e.target.value.split('-') as [
                'created_at' | 'start_date' | 'customer_name',
                'asc' | 'desc'
              ];
              onSortingChange({ field, direction });
            }}
            className="input-field"
          >
            <option value="created_at-desc">Newest First</option>
            <option value="created_at-asc">Oldest First</option>
            <option value="customer_name-asc">Customer A-Z</option>
            <option value="customer_name-desc">Customer Z-A</option>
            <option value="start_date-desc">Start Date (Latest)</option>
            <option value="start_date-asc">Start Date (Earliest)</option>
          </select>
        </div>

        {/* Clear Filters */}
        {hasActiveFilters && (
          <button
            type="button"
            onClick={onClear}
            className="flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium
                       text-secondary-600 hover:text-secondary-800 hover:bg-secondary-100
                       rounded-lg transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
            Clear
          </button>
        )}
      </div>
    </div>
  );
}